/**
 * Polls personal lesson generation jobs and drives persistent Sonner toasts.
 *
 * Lifecycle (4-stage progress):
 *   planning → toast.loading("Stage 1/4: Planning your lesson...")
 *   writing  → toast.loading("Stage 2/4: Writing lesson about Topic (33%)")
 *   images   → toast.loading("Stage 3/4: Finding images (50%)")
 *   saving   → toast.loading("Stage 4/4: Saving your lesson...")
 *   complete → toast.success("Lesson title", action: "Start lesson")
 *   failed   → toast.error("Generation failed")
 *
 * Percentage interpolates smoothly between poll updates (3s interval).
 * On mount, also recovers completed-but-unacknowledged jobs (refresh recovery).
 * Each job gets its own toast keyed by job ID, supporting concurrent generations.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/shared/auth/AuthProvider';
import { useMyJobs, useAcknowledgeJob } from '@/shared/api/hooks';
import { useSessionGuard } from '@/shared/hooks/useSessionGuard';
import { api } from '@/shared/api/client';
import type { BackgroundJob } from '@formency/shared';

// ─── Progress shape written by the backend ───────────────────────────────────

interface PersonalLessonProgress {
  phase?: 'planning' | 'writing' | 'images' | 'saving' | 'complete';
  percent?: number;
  lessonId?: string;
  topicSummary?: string;
  message?: string;
}

// ─── Toast state tracking ────────────────────────────────────────────────────

type ToastPhase = 'loading' | 'done';

interface InterpolationState {
  target: number;
  display: number;
}

function getLoadingDescription(
  progress: PersonalLessonProgress | null,
  displayPercent: number
): string {
  if (!progress?.phase) return 'Starting generation...';

  // Show message override (e.g. "AI slow to respond, retrying…")
  if (progress.message) return progress.message;

  const pct = Math.round(displayPercent);
  const topic = progress.topicSummary;

  switch (progress.phase) {
    case 'planning':
      return 'Stage 1/4: Planning your lesson...';
    case 'writing': {
      const label = topic ? `Writing lesson about ${topic}` : 'Writing lesson';
      return pct > 0 ? `Stage 2/4: ${label} (${pct}%)` : `Stage 2/4: ${label}...`;
    }
    case 'images':
      return pct > 0 ? `Stage 3/4: Finding images (${pct}%)` : 'Stage 3/4: Finding images...';
    case 'saving':
      return 'Stage 4/4: Saving your lesson...';
    default:
      return 'Generating lesson...';
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function usePersonalLessonToasts() {
  const { isAuthed } = useAuth();
  const navigate = useNavigate();
  const { guardSession } = useSessionGuard();
  const acknowledge = useAcknowledgeJob();
  const toastPhaseRef = useRef(new Map<string, ToastPhase>());
  const prevProcessingIdsRef = useRef(new Set<string>());
  const mountRecoveryDoneRef = useRef(false);
  const interpolationRef = useRef(new Map<string, InterpolationState>());
  const lastPhaseRef = useRef(new Map<string, string>());
  const lastDescriptionRef = useRef(new Map<string, string>());
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Poll active (pending + processing) jobs ─────────────────────────────
  // Active generation: poll every 3s for real-time progress
  // Idle: stop polling (refetchOnWindowFocus handles tab returns)

  const { data: activeJobsRaw } = useMyJobs(
    { type: 'personal_lesson_learn' },
    {
      refetchInterval: (query) => {
        const jobs = query.state.data;
        const hasActive = jobs?.some((j) => j.status === 'pending' || j.status === 'processing');
        return hasActive ? 3000 : false;
      },
      enabled: isAuthed,
    }
  );

  // Filter to only pending/processing jobs (ignore completed/failed — handled by mount recovery)
  const processingJobs = activeJobsRaw?.filter(
    (j) => j.status === 'pending' || j.status === 'processing'
  );

  // ── Toast builders (stable via useCallback) ──────────────────────────────

  const showSuccessToast = useCallback(
    (job: BackgroundJob) => {
      const result = job.result as { lessonId?: string; title?: string } | null;
      const lessonId = result?.lessonId ?? '';
      const title = result?.title ?? 'Your lesson is ready';

      if (!lessonId) return;

      toast.success(title, {
        id: job.id,
        description: 'Ready to start learning',
        duration: Infinity,
        action: {
          label: 'Start lesson',
          onClick: () => {
            acknowledge.mutate(job.id);
            guardSession({
              lessonId,
              onProceed: () => navigate(`/lessons/unfiled/${lessonId}`),
            });
          },
        },
        onDismiss: () => acknowledge.mutate(job.id),
      });
      toastPhaseRef.current.set(job.id, 'done');
      interpolationRef.current.delete(job.id);
      lastPhaseRef.current.delete(job.id);
      lastDescriptionRef.current.delete(job.id);
    },
    [acknowledge, guardSession, navigate]
  );

  const showErrorToast = useCallback(
    (job: BackgroundJob) => {
      toast.error('Generation failed', {
        id: job.id,
        description: job.errorMessage ?? 'Something went wrong. Please try again.',
        duration: Infinity,
        onDismiss: () => acknowledge.mutate(job.id),
      });
      toastPhaseRef.current.set(job.id, 'done');
      interpolationRef.current.delete(job.id);
      lastPhaseRef.current.delete(job.id);
      lastDescriptionRef.current.delete(job.id);
    },
    [acknowledge]
  );

  const showTerminalToast = useCallback(
    (job: BackgroundJob) => {
      const result = job.result as { lessonId?: string } | null;
      if (job.status === 'completed' && result?.lessonId) {
        showSuccessToast(job);
      } else if (job.status === 'failed') {
        showErrorToast(job);
      }
    },
    [showSuccessToast, showErrorToast]
  );

  // ── React to processing job changes ──────────────────────────────────────

  useEffect(() => {
    if (!processingJobs) return;

    const currentIds = new Set(processingJobs.map((j) => j.id));

    // Detect jobs that disappeared from processing (finished)
    for (const prevId of prevProcessingIdsRef.current) {
      if (!currentIds.has(prevId) && toastPhaseRef.current.get(prevId) !== 'done') {
        // Job finished — fetch final state
        void api.get<BackgroundJob>(`/me/jobs/${prevId}`).then((job) => {
          showTerminalToast(job);
        });
      }
    }

    // Update interpolation targets for current processing jobs
    for (const job of processingJobs) {
      const progress = job.progress as PersonalLessonProgress | null;

      // Set interpolation target
      const target = progress?.percent ?? 0;

      // Snap interpolation on phase change — prevents old stage's percentage bleeding into new stage
      const currentPhase = progress?.phase ?? '';
      const prevPhase = lastPhaseRef.current.get(job.id);
      if (prevPhase && prevPhase !== currentPhase) {
        interpolationRef.current.set(job.id, { target, display: target });
      }
      lastPhaseRef.current.set(job.id, currentPhase);

      const existing = interpolationRef.current.get(job.id);
      if (existing) {
        existing.target = target;
      } else {
        // First encounter: if the job is already well into generation (e.g. page refresh),
        // snap to current value. If it's fresh (0%), start from 0 for smooth ramp-up.
        const snapToTarget = target > 5;
        interpolationRef.current.set(job.id, { target, display: snapToTarget ? target : 0 });
      }

      // Show or update loading toast — re-render on every poll so phase transitions
      // (e.g. planning → writing) are reflected immediately, not just when % changes.
      const desc = getLoadingDescription(progress, existing?.display ?? target);
      const isNew = toastPhaseRef.current.get(job.id) !== 'loading';
      if (isNew || lastDescriptionRef.current.get(job.id) !== desc) {
        toast.loading(desc, { id: job.id, duration: Infinity });
        lastDescriptionRef.current.set(job.id, desc);
        if (isNew) toastPhaseRef.current.set(job.id, 'loading');
      }
    }

    prevProcessingIdsRef.current = currentIds;
  }, [processingJobs, showTerminalToast, showSuccessToast]);

  // ── Smooth percentage interpolation ticker ───────────────────────────────

  useEffect(() => {
    // Start ticker when we have loading toasts, stop when we don't
    const hasLoadingJobs = processingJobs && processingJobs.length > 0;

    if (hasLoadingJobs && !tickerRef.current) {
      tickerRef.current = setInterval(() => {
        for (const [jobId, state] of interpolationRef.current) {
          if (toastPhaseRef.current.get(jobId) !== 'loading') continue;

          // Move toward target — cover the gap over ~2s (at 200ms ticks = 10 steps)
          let moved = false;
          if (state.display < state.target) {
            const remaining = state.target - state.display;
            state.display = state.display + Math.max(remaining * 0.15, 0.5);
            if (state.display > state.target) state.display = state.target;
            moved = true;
          }

          // Find the matching job to get progress for the description
          const job = processingJobs?.find((j) => j.id === jobId);
          if (!job) continue;
          const progress = job.progress as PersonalLessonProgress | null;
          const desc = getLoadingDescription(progress, state.display);

          // Update toast if percentage moved OR if text changed (phase transition)
          if (moved || lastDescriptionRef.current.get(jobId) !== desc) {
            toast.loading(desc, { id: jobId, duration: Infinity });
            lastDescriptionRef.current.set(jobId, desc);
          }
        }
      }, 200);
    }

    if (!hasLoadingJobs && tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }

    return () => {
      if (tickerRef.current) {
        clearInterval(tickerRef.current);
        tickerRef.current = null;
      }
    };
  }, [processingJobs]);

  // ── Mount recovery: check for unacknowledged completed/failed jobs ──────

  useEffect(() => {
    if (!isAuthed || mountRecoveryDoneRef.current) return;
    mountRecoveryDoneRef.current = true;

    // Only recover the most recent unacknowledged job — older ones are in the library
    const recover = async () => {
      const [completed, failed] = await Promise.all([
        api.get<BackgroundJob[]>('/me/jobs', {
          type: 'personal_lesson_learn',
          status: 'completed',
        }),
        api.get<BackgroundJob[]>('/me/jobs', {
          type: 'personal_lesson_learn',
          status: 'failed',
        }),
      ]);

      // Find the single most recent unacknowledged terminal job
      const allTerminal = [...completed, ...failed]
        .filter((j) => !j.acknowledgedAt && !toastPhaseRef.current.has(j.id))
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

      const latest = allTerminal[0];
      if (!latest) return;

      const result = latest.result as { lessonId?: string } | null;
      if (latest.status === 'failed') {
        showErrorToast(latest);
      } else if (result?.lessonId) {
        showSuccessToast(latest);
      }
    };

    void recover().catch(() => {
      // Best-effort recovery — not critical
    });
  }, [isAuthed, showSuccessToast, showErrorToast]);
}
